import { useState, type FormEvent } from "react";
import { Briefcase, Plus, Upload, X } from "lucide-react";
import { Alert, Badge, Button, Card, CardBody, EmptyState, Field, Input, PageHeader, Select, Textarea } from "@fellowflats/ui";
import { ImportLeadsReplySchema, LeadDetailSchema, LeadSchema, LeadsSchema, type Lead, type LeadDetail } from "@fellowflats/contracts/admin";
import { api } from "../lib/api";
import { parseCsv, rowsToLeads, type ImportRow } from "../lib/csv";
import { describe, useApi } from "../lib/use-api";
import { ActionButton, Loaded, day } from "../components/bits";

const STATUSES = ["NEW", "CONTACTED", "REPLIED", "INTERESTED", "SIGNED_UP", "NOT_INTERESTED"] as const;
const KINDS = ["LANDLORD", "AGENT", "HOUSING_OFFICE", "OTHER"] as const;
const words = (v: string) => v.toLowerCase().replace(/_/g, " ");
const statusTone = {
  NEW: "info",
  CONTACTED: "neutral",
  REPLIED: "primary",
  INTERESTED: "warning",
  SIGNED_UP: "success",
  NOT_INTERESTED: "neutral",
} as const;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * The CRM: landlords to list, agents and campus housing offices to refer
 * renters. Open leads first, follow-ups that are due on top.
 */
export function Leads() {
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    kind: "",
    due: false,
  });
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [panel, setPanel] = useState<"none" | "add" | "import">("none");
  const query = new URLSearchParams();
  if (term) query.set("q", term);
  if (filters.status) query.set("status", filters.status);
  if (filters.kind) query.set("kind", filters.kind);
  if (filters.due) query.set("due", "1");
  const qs = query.toString();
  const state = useApi(() => api("GET", `/admin/leads${qs ? `?${qs}` : ""}`, LeadsSchema), qs);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leads"
        description="Landlords to list, and agents and campus offices who send renters."
        actions={
          <>
            <Button size="sm" variant="outline" onClick={() => setPanel(panel === "import" ? "none" : "import")}>
              <Upload size={14} aria-hidden="true" /> Import CSV
            </Button>
            <Button size="sm" onClick={() => setPanel(panel === "add" ? "none" : "add")}>
              <Plus size={14} aria-hidden="true" /> Add lead
            </Button>
          </>
        }
      />

      {panel === "add" && (
        <AddLead
          onDone={(id) => {
            setPanel("none");
            state.reload();
            setOpen(id);
          }}
        />
      )}
      {panel === "import" && <ImportLeads onDone={state.reload} />}

      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setTerm(filters.q.trim());
        }}
      >
        <Input
          className="max-w-xs"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          placeholder="Search name, company, email, area"
          aria-label="Search leads"
        />
        <div className="w-44">
          <Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} aria-label="Status">
            <option value="">Any status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {words(s)}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-44">
          <Select value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })} aria-label="Kind">
            <option value="">Any kind</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {words(k)}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={filters.due} onChange={(e) => setFilters({ ...filters, due: e.target.checked })} className="accent-primary" />{" "}
          Follow-up due
        </label>
      </form>

      <div className={open ? "grid gap-6 xl:grid-cols-[1fr_380px]" : ""}>
        <Loaded state={state}>
          {(leads) =>
            leads.length === 0 ? (
              <EmptyState icon={Briefcase} title="No leads here" body="Add one, or import the outreach tracker as a CSV." />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Lead</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Follow up</th>
                      <th className="px-3 py-2">Last contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr
                        key={l.id}
                        onClick={() => setOpen(l.id)}
                        className={`cursor-pointer border-t border-border hover:bg-muted ${open === l.id ? "bg-muted" : ""}`}
                      >
                        <td className="px-3 py-2">
                          <p className="font-medium">{l.name}</p>
                          <p className="text-muted-foreground">
                            {words(l.kind)}
                            {l.area ? ` · ${l.area}` : ""}
                          </p>
                        </td>
                        <td className="px-3 py-2">
                          <Badge tone={statusTone[l.status]}>{words(l.status)}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          {l.nextFollowUpAt ? (
                            <span className={l.nextFollowUpAt <= today() ? "font-medium text-warning" : ""}>{day(`${l.nextFollowUpAt}T12:00:00Z`)}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{day(l.lastContactedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Loaded>
        {open && <LeadPanel key={open} id={open} onClose={() => setOpen(null)} onChanged={state.reload} />}
      </div>
    </div>
  );
}

function AddLead({ onDone }: { onDone: (id: string) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    setBusy(true);
    setError(null);
    try {
      const lead = await api("POST", "/admin/leads", LeadSchema, {
        ...f,
        nextFollowUpAt: f.nextFollowUpAt || null,
      });
      onDone(lead.id);
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardBody className="p-5">
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" htmlFor="name" required>
            <Input id="name" name="name" required maxLength={200} />
          </Field>
          <Field label="Kind" htmlFor="kind">
            <Select id="kind" name="kind" defaultValue="LANDLORD">
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {words(k)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Email" htmlFor="email">
            <Input id="email" name="email" type="email" />
          </Field>
          <Field label="Phone" htmlFor="phone">
            <Input id="phone" name="phone" />
          </Field>
          <Field label="Area or campus" htmlFor="area">
            <Input id="area" name="area" />
          </Field>
          <Field label="Where we found them" htmlFor="source">
            <Input id="source" name="source" placeholder="Kijiji, Facebook, referral..." />
          </Field>
          <Field label="Follow up on" htmlFor="nextFollowUpAt">
            <Input id="nextFollowUpAt" name="nextFollowUpAt" type="date" />
          </Field>
          <div className="flex items-end gap-2 sm:col-span-2">
            <Button type="submit" loading={busy}>
              Add lead
            </Button>
            {error && <Alert>{error}</Alert>}
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function ImportLeads({ onDone }: { onDone: () => void }) {
  const [kind, setKind] = useState<ImportRow["kind"]>("LANDLORD");
  const [parsed, setParsed] = useState<{
    leads: ImportRow[];
    dropped: number;
    file: string;
  } | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Card>
      <CardBody className="space-y-3 p-5">
        <p className="text-sm text-muted-foreground">
          A CSV with a header row. Columns understood: Name (or Landlord Name, Company, School), Kind, Email, Phone, Website, Area (or City, Area / Campus),
          Source, Listing Link, Status, Notes, Follow-up Date. The landlord outreach tracker imports as it is; for the agent list run{" "}
          <code className="font-mono text-xs">node apps/admin/scripts/outreach-md-to-csv.mjs agent-outreach-list.md &gt; leads.csv</code> first. Duplicates
          (same email, or same name and phone) are skipped.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-64">
            <Select value={kind} onChange={(e) => setKind(e.target.value as ImportRow["kind"])} aria-label="Kind for rows without one">
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  Rows without a kind: {words(k)}
                </option>
              ))}
            </Select>
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            aria-label="CSV file"
            className="text-sm"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setResult(null);
              setParsed({
                ...rowsToLeads(parseCsv(await file.text()), kind),
                file: file.name,
              });
            }}
          />
        </div>
        {parsed && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm">
              {parsed.file}: {parsed.leads.length} lead(s) to import
              {parsed.dropped ? `, ${parsed.dropped} row(s) without a name or a template row left out` : ""}.
            </span>
            {parsed.leads.length > 0 && (
              <ActionButton
                variant="primary"
                run={async () => {
                  setError(null);
                  let created = 0;
                  let skipped = 0;
                  // The API takes 500 at a time.
                  for (let i = 0; i < parsed.leads.length; i += 500) {
                    const r = await api("POST", "/admin/leads/import", ImportLeadsReplySchema, { leads: parsed.leads.slice(i, i + 500) }).catch((e) => {
                      setError(describe(e));
                      throw e;
                    });
                    created += r.created;
                    skipped += r.skipped;
                  }
                  setResult(`${created} added, ${skipped} already there.`);
                  setParsed(null);
                  onDone();
                }}
              >
                Import
              </ActionButton>
            )}
          </div>
        )}
        {result && <Alert tone="success">{result}</Alert>}
        {error && <Alert>{error}</Alert>}
      </CardBody>
    </Card>
  );
}

function LeadPanel({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const state = useApi(() => api("GET", `/admin/leads/${id}`, LeadDetailSchema), id);
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const shown = lead ?? state.data;
  const after = (l: LeadDetail | Lead) => {
    if ("activities" in l) setLead(l);
    else state.reload();
    onChanged();
  };

  return (
    <Card className="h-fit xl:sticky xl:top-6">
      <CardBody className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-semibold">{shown?.name ?? "Lead"}</h2>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </Button>
        </div>
        <Loaded state={{ ...state, data: shown }}>
          {(l) => (
            <>
              <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 text-sm">
                <dt className="text-muted-foreground">Kind</dt>
                <dd>{words(l.kind)}</dd>
                {l.company && (
                  <>
                    <dt className="text-muted-foreground">Company</dt>
                    <dd>{l.company}</dd>
                  </>
                )}
                {l.email && (
                  <>
                    <dt className="text-muted-foreground">Email</dt>
                    <dd>
                      <a className="text-primary hover:underline" href={`mailto:${l.email}`}>
                        {l.email}
                      </a>
                    </dd>
                  </>
                )}
                {l.phone && (
                  <>
                    <dt className="text-muted-foreground">Phone</dt>
                    <dd>
                      <a className="text-primary hover:underline" href={`tel:${l.phone.replace(/[^\d+]/g, "")}`}>
                        {l.phone}
                      </a>
                    </dd>
                  </>
                )}
                {l.website && (
                  <>
                    <dt className="text-muted-foreground">Website</dt>
                    <dd className="truncate">
                      <a
                        className="text-primary hover:underline"
                        href={l.website.startsWith("http") ? l.website : `https://${l.website}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {l.website}
                      </a>
                    </dd>
                  </>
                )}
                {l.listingUrl && (
                  <>
                    <dt className="text-muted-foreground">Their ad</dt>
                    <dd className="truncate">
                      <a className="text-primary hover:underline" href={l.listingUrl} target="_blank" rel="noreferrer">
                        {l.listingUrl}
                      </a>
                    </dd>
                  </>
                )}
                <dt className="text-muted-foreground">Area</dt>
                <dd>{l.area ?? "-"}</dd>
                <dt className="text-muted-foreground">Source</dt>
                <dd>{l.source ?? "-"}</dd>
                <dt className="text-muted-foreground">Owner</dt>
                <dd>{l.ownerEmail ?? "-"}</dd>
              </dl>

              <div className="grid grid-cols-2 gap-2">
                <Field label="Status" htmlFor="lead-status">
                  <Select
                    id="lead-status"
                    value={l.status}
                    onChange={(e) =>
                      void api("PUT", `/admin/leads/${l.id}`, LeadSchema, {
                        status: e.target.value,
                      }).then(after)
                    }
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {words(s)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Follow up on" htmlFor="lead-follow">
                  <Input
                    id="lead-follow"
                    type="date"
                    defaultValue={l.nextFollowUpAt ?? ""}
                    onBlur={(e) => {
                      if ((e.target.value || null) !== l.nextFollowUpAt)
                        void api("PUT", `/admin/leads/${l.id}`, LeadSchema, {
                          nextFollowUpAt: e.target.value || null,
                        }).then(after);
                    }}
                  />
                </Field>
              </div>

              <LogActivity id={l.id} onLogged={after} />

              <ol className="space-y-2 border-t border-border pt-3">
                {l.activities.map((a) => (
                  <li key={a.id} className="text-sm">
                    <p>
                      <Badge tone={a.kind === "STATUS" ? "neutral" : "primary"}>{words(a.kind)}</Badge>{" "}
                      {a.kind === "STATUS" ? words(a.body.replace(" -> ", " to ")) : a.body}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {day(a.createdAt)} · {a.authorEmail ?? "-"}
                    </p>
                  </li>
                ))}
                {l.activities.length === 0 && <li className="text-sm text-muted-foreground">No history yet.</li>}
              </ol>
              {l.notes && <p className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">{l.notes}</p>}
            </>
          )}
        </Loaded>
      </CardBody>
    </Card>
  );
}

function LogActivity({ id, onLogged }: { id: string; onLogged: (l: LeadDetail) => void }) {
  const [kind, setKind] = useState<"NOTE" | "EMAIL" | "CALL" | "MESSAGE">("CALL");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="space-y-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        setBusy(true);
        setError(null);
        try {
          onLogged(await api("POST", `/admin/leads/${id}/activities`, LeadDetailSchema, { kind, body }));
          setBody("");
        } catch (err) {
          setError(describe(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        maxLength={4000}
        placeholder="What happened? A call, an email, a note."
        aria-label="Activity"
      />
      <div className="flex gap-2">
        <div className="w-32">
          <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} aria-label="Activity kind">
            <option value="CALL">call</option>
            <option value="EMAIL">email</option>
            <option value="MESSAGE">message</option>
            <option value="NOTE">note</option>
          </Select>
        </div>
        <Button type="submit" size="sm" loading={busy}>
          Log it
        </Button>
      </div>
      {error && <Alert>{error}</Alert>}
    </form>
  );
}
